import React, { useState, useEffect } from 'react';
import { userPlanAPI, userAPI, planAPI } from '../services/api';
import { UserPlan, User, Plan } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Collapse,
  Card,
  CardContent
} from '@mui/material';
import { Edit, Delete, Refresh, FilterList, Clear, Add, Visibility, Person, CreditCard, Receipt, Cancel } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

// Define test subscription template type
interface TestSubscriptionTemplate {
  name: string;
  description: string;
  paymentMethod: string;
  amount: number;
  currency: string;
  platform: string;
}

const UserPlans: React.FC = () => {
  const [userPlans, setUserPlans] = useState<UserPlan[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedUserPlan, setSelectedUserPlan] = useState<UserPlan | null>(null);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterForm, setFilterForm] = useState({
    userId: '',
    planId: '',
    status: '',
    paymentMethod: '',
    platform: ''
  });
  
  const [formError, setFormError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [showTestSubscriptionModal, setShowTestSubscriptionModal] = useState(false);
  const [creatingTestSubscription, setCreatingTestSubscription] = useState(false);
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [testSubscriptionForm, setTestSubscriptionForm] = useState({
    userId: '',
    planId: '',
    paymentMethod: 'in_app_purchase',
    amount: '',
    platform: 'android',
    transactionId: ''
  });

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'expired', label: 'Expired' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'pending', label: 'Pending' }
  ];

  const paymentMethods = [
    { value: 'in_app_purchase', label: 'In-App Purchase' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'upi', label: 'UPI' },
    { value: 'net_banking', label: 'Net Banking' }
  ];

  const platforms = [
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
    { value: 'web', label: 'Web' }
  ];

  useEffect(() => {
    fetchUserPlans();
    fetchUsers();
    fetchPlans();
  }, []);

  const fetchUserPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await userPlanAPI.getAllUserPlans();
      setUserPlans(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load user plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await userAPI.getAllUsers();
      setUsers(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load users:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await planAPI.getAllPlans();
      setPlans(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      console.error('Failed to load plans:', error);
    }
  };

  const applyFilters = async () => {
    setFilterLoading(true);
    setError(null);
    try {
      const res = await userPlanAPI.getAllUserPlans();
      let filteredUserPlans = res.data;
      
      if (filterForm.userId) {
        filteredUserPlans = filteredUserPlans.filter(userPlan => 
          userPlan.userId.toLowerCase().includes(filterForm.userId.toLowerCase())
        );
      }
      
      if (filterForm.planId) {
        filteredUserPlans = filteredUserPlans.filter(userPlan => 
          userPlan.planId.toLowerCase().includes(filterForm.planId.toLowerCase())
        );
      }
      
      if (filterForm.status) {
        filteredUserPlans = filteredUserPlans.filter(userPlan => 
          userPlan.status === filterForm.status
        );
      }
      
      if (filterForm.paymentMethod) {
        filteredUserPlans = filteredUserPlans.filter(userPlan => 
          userPlan.paymentMethod === filterForm.paymentMethod
        );
      }
      
      if (filterForm.platform) {
        filteredUserPlans = filteredUserPlans.filter(userPlan => 
          userPlan.platform === filterForm.platform
        );
      }
      
      setUserPlans(filteredUserPlans);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to apply filters');
    } finally {
      setFilterLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterForm({
      userId: '',
      planId: '',
      status: '',
      paymentMethod: '',
      platform: ''
    });
    fetchUserPlans();
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    setFilterForm({
      ...filterForm,
      [e.target.name]: e.target.value
    });
  };

  const handleViewUserPlan = (userPlan: UserPlan) => {
    setSelectedUserPlan(userPlan);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUserPlan(null);
  };

  const handleCancelUserPlan = async (id: string) => {
    if (!window.confirm('Are you sure you want to cancel this user plan?')) return;
    
    try {
      await userPlanAPI.cancelUserPlan(id);
      setSuccessMsg('User plan cancelled successfully!');
      fetchUserPlans();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to cancel user plan');
    }
  };

  const handleDeleteUserPlan = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this user plan? This action cannot be undone.')) return;
    
    setDeletingId(id);
    try {
      await userPlanAPI.deleteUserPlan(id);
      setSuccessMsg('User plan deleted successfully!');
      fetchUserPlans();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete user plan');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
      case 'EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  const isExpired = (endDate?: string) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  // Test subscription templates
  const testSubscriptionTemplates: TestSubscriptionTemplate[] = [
    {
      name: 'Active Premium Monthly',
      description: 'Active premium monthly subscription for testing',
      paymentMethod: 'in_app_purchase',
      amount: 199,
      currency: 'INR',
      platform: 'android'
    },
    {
      name: 'Active Premium Quarterly',
      description: 'Active premium quarterly subscription for testing',
      paymentMethod: 'in_app_purchase',
      amount: 499,
      currency: 'INR',
      platform: 'ios'
    },
    {
      name: 'Expired Basic Monthly',
      description: 'Expired basic monthly subscription for testing',
      paymentMethod: 'credit_card',
      amount: 99,
      currency: 'INR',
      platform: 'web'
    },
    {
      name: 'Cancelled Premium Yearly',
      description: 'Cancelled premium yearly subscription for testing',
      paymentMethod: 'upi',
      amount: 1499,
      currency: 'INR',
      platform: 'android'
    }
  ];

  const createTestSubscription = async (template: TestSubscriptionTemplate) => {
    setCreatingTestSubscription(true);
    try {
      // Find a random user and plan for testing
      const randomUser = users[Math.floor(Math.random() * users.length)];
      const randomPlan = plans[Math.floor(Math.random() * plans.length)];
      
      if (!randomUser || !randomPlan) {
        setError('No users or plans available for test subscription');
        return;
      }

      const userPlanData = {
        userId: randomUser.id!,
        planId: randomPlan.id!,
        status: 'ACTIVE',
        paymentMethod: template.paymentMethod,
        amount: template.amount,
        currency: template.currency,
        platform: template.platform
      };

      await userPlanAPI.createUserPlan(userPlanData);
      setSuccessMsg('Test subscription created successfully');
      await fetchUserPlans();
      setShowTestSubscriptionModal(false);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to create test subscription');
    } finally {
      setCreatingTestSubscription(false);
    }
  };

  const handleTestSubscriptionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setTestSubscriptionForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const createCustomTestSubscription = async () => {
    if (!testSubscriptionForm.userId || !testSubscriptionForm.planId || !testSubscriptionForm.amount) {
      setError('Please fill in all required fields');
      return;
    }

    setCreatingTestSubscription(true);
    try {
      const subscriptionData = {
        userId: testSubscriptionForm.userId,
        planId: testSubscriptionForm.planId,
        paymentMethod: testSubscriptionForm.paymentMethod,
        amount: parseFloat(testSubscriptionForm.amount),
        platform: testSubscriptionForm.platform,
        transactionId: testSubscriptionForm.transactionId || `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };

      await userPlanAPI.createUserPlan(subscriptionData);
      setSuccessMsg('Custom test subscription created successfully!');
      setShowTestSubscriptionModal(false);
      setTestSubscriptionForm({
        userId: '',
        planId: '',
        paymentMethod: 'in_app_purchase',
        amount: '',
        platform: 'android',
        transactionId: ''
      });
      fetchUserPlans();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to create custom test subscription');
    } finally {
      setCreatingTestSubscription(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          User Plans Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUserPlans}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<CreditCard />}
            onClick={() => setShowTestSubscriptionModal(true)}
          >
            Create Test Subscription
          </Button>
          <Button
            variant={showExpiredOnly ? "contained" : "outlined"}
            color="warning"
            onClick={() => {
              setShowExpiredOnly(!showExpiredOnly);
              fetchUserPlans();
            }}
          >
            {showExpiredOnly ? 'Show All Plans' : 'Show Expired Only'}
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter User Plans
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="User ID"
                  name="userId"
                  value={filterForm.userId}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Plan ID"
                  name="planId"
                  value={filterForm.planId}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={filterForm.status}
                    onChange={handleFilterChange}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    {statusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    name="paymentMethod"
                    value={filterForm.paymentMethod}
                    onChange={handleFilterChange}
                    label="Payment Method"
                  >
                    <MenuItem value="">All</MenuItem>
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Platform</InputLabel>
                  <Select
                    name="platform"
                    value={filterForm.platform}
                    onChange={handleFilterChange}
                    label="Platform"
                  >
                    <MenuItem value="">All</MenuItem>
                    {platforms.map((platform) => (
                      <MenuItem key={platform.value} value={platform.value}>
                        {platform.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={applyFilters}
                    disabled={filterLoading}
                    size="small"
                  >
                    {filterLoading ? <CircularProgress size={16} /> : 'Apply'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                    startIcon={<Clear />}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>User ID</TableCell>
                  <TableCell>Plan ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userPlans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">No user plans found.</TableCell>
                  </TableRow>
                ) : userPlans.map(userPlan => (
                  <TableRow key={userPlan.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {userPlan.userId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={userPlan.planId} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userPlan.status} 
                        color={getStatusColor(userPlan.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatPrice(userPlan.amount, userPlan.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userPlan.paymentMethod} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={userPlan.platform} 
                        size="small" 
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      {userPlan.startDate ? formatDate(userPlan.startDate) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {userPlan.endDate ? formatDate(userPlan.endDate) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewUserPlan(userPlan)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleCancelUserPlan(userPlan.id!)}
                            disabled={userPlan.status === 'cancelled'}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteUserPlan(userPlan.id!)}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* User Plan Details Dialog */}
      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>
          User Plan Details
        </DialogTitle>
        <DialogContent>
          {selectedUserPlan && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="User ID"
                  value={selectedUserPlan.userId}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Plan ID"
                  value={selectedUserPlan.planId}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedUserPlan.status}
                    label="Status"
                    inputProps={{ readOnly: true }}
                  >
                    {statusOptions.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  value={formatPrice(selectedUserPlan.amount, selectedUserPlan.currency)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Method"
                  value={selectedUserPlan.paymentMethod}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Platform"
                  value={selectedUserPlan.platform}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Transaction ID"
                  value={selectedUserPlan.transactionId || 'N/A'}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  value={formatDate(selectedUserPlan.startDate)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  value={formatDate(selectedUserPlan.endDate)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Created At"
                  value={formatDate(selectedUserPlan.createdAt)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Updated At"
                  value={formatDate(selectedUserPlan.updatedAt)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Subscription Creation Modal */}
      <Dialog open={showTestSubscriptionModal} onClose={() => setShowTestSubscriptionModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CreditCard color="secondary" />
            Create Test Subscriptions for Testing
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Quickly create test user subscriptions to verify subscription functionality. These subscriptions are designed for testing purposes.
          </Typography>
          
          {/* Quick Templates */}
          <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Quick Templates</Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {testSubscriptionTemplates.map((template, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      boxShadow: 3,
                      border: '1px solid',
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => createTestSubscription(template)}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {template.description}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ₹{template.amount}
                      </Typography>
                      <Chip label={template.platform} size="small" variant="outlined" />
                    </Box>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Chip label={template.paymentMethod} size="small" variant="outlined" />
                      <Chip label={template.platform} size="small" color="primary" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Payment: {template.paymentMethod} • {template.currency} {template.amount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Platform: {template.platform}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Custom Form */}
          <Typography variant="h6" sx={{ mb: 2 }}>Custom Test Subscription</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="User ID"
                name="userId"
                value={testSubscriptionForm.userId}
                onChange={handleTestSubscriptionFormChange}
                required
                helperText="Enter the user ID for the test subscription"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan ID"
                name="planId"
                value={testSubscriptionForm.planId}
                onChange={handleTestSubscriptionFormChange}
                required
                helperText="Enter the plan ID (e.g., premium_monthly)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={testSubscriptionForm.amount}
                onChange={handleTestSubscriptionFormChange}
                required
                helperText="Enter the subscription amount"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="paymentMethod"
                  value={testSubscriptionForm.paymentMethod}
                  onChange={handleTestSubscriptionFormChange}
                  label="Payment Method"
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Platform</InputLabel>
                <Select
                  name="platform"
                  value={testSubscriptionForm.platform}
                  onChange={handleTestSubscriptionFormChange}
                  label="Platform"
                >
                  {platforms.map((platform) => (
                    <MenuItem key={platform.value} value={platform.value}>
                      {platform.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Transaction ID (Optional)"
                name="transactionId"
                value={testSubscriptionForm.transactionId}
                onChange={handleTestSubscriptionFormChange}
                helperText="Leave empty for auto-generated ID"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTestSubscriptionModal(false)}>
            Cancel
          </Button>
          <Button 
            onClick={createCustomTestSubscription}
            variant="contained"
            disabled={creatingTestSubscription || !testSubscriptionForm.userId || !testSubscriptionForm.planId || !testSubscriptionForm.amount}
          >
            {creatingTestSubscription ? <CircularProgress size={16} /> : 'Create Custom'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPlans; 